import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

const StyledButton = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;

  width: 100%;

  padding-top: ${theme.spaces.smallX + 1}px;
  padding-bottom: ${theme.spaces.smallX + 1}px;
  padding-left: ${theme.spaces.regular}px;
  padding-right: ${theme.spaces.regular}px;
  border-radius: 12px;
  border-width: 1px;
  border-style: solid;
  border-color: ${theme.colors.gray.secondary};
  background-color: ${theme.colors.gray.secondary};

  font-family: 'Inter';
  font-size: ${theme.fontSizes.small}px;
  font-weight: 600;
  line-height: 14px;
  color: ${theme.colors.white};

  transition: all 0.3s ease-out;

  cursor: pointer;

  .prefix,
  .suffix {
    display: inline-flex;
    align-items: center;
  }

  .prefix {
    margin-right: ${theme.spaces.small - 2}px;
  }

  .suffix {
    margin-left: ${theme.spaces.small - 2}px;
  }

  ${({ color }) =>
    color &&
    css`
      background-color: ${color};
      border-color: ${color};
    `}

  ${({ outlined }) =>
    outlined &&
    css`
      background-color: ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
      border-color: ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
      color: ${theme.colors.gray.secondary};
    `}
  
  ${({ outlined, color }) =>
    outlined &&
    color &&
    css`
      background-color: ${hexToRGBA(color, 0.16)};
      border-color: ${hexToRGBA(color, 0.16)};
      color: ${color};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}

  ${({ small }) =>
    small &&
    css`
      padding: ${theme.spaces.smallX - 3}px;
      border-radius: 8px;
    `}
`;

// eslint-disable-next-line react/display-name
const Button = forwardRef(({ children, type, prefix, suffix, color, outlined, disabled, small, ...restProps }, ref) => {
  return (
    <StyledButton
      ref={ref}
      type={type || 'button'}
      color={color}
      outlined={outlined}
      disabled={disabled}
      small={small}
      {...restProps}
    >
      {prefix && <span className="prefix">{prefix}</span>}
      {children}
      {suffix && <span className="suffix">{suffix}</span>}
    </StyledButton>
  );
});

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  type: PropTypes.string,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  color: PropTypes.string,
  outlined: PropTypes.bool,
  disabled: PropTypes.bool,
  small: PropTypes.bool,
};

export default Button;
